customElements.whenDefined('search-form').then(() => {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.requestSequence = 0;
      this.closeBlurTimeout = null;
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.setupEventListeners();
      // Focus and input may arrive before the deferred component is ready.
      if (document.activeElement === this.input) this.onFocus();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('input', debounce((event) => { this.onChange(event);}, 300).bind(this));
      this.productTypes?.addEventListener('change', () => {
        this.close();
        this.onChange();
      });
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.closest('.template-search__search') || this.closest('.header__search-bar')) {
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      this.addEventListener('click', (event) => {
        this.captureProductSearch(event);
        if (event.target.closest('[data-predictive-search-retry]')) {
          event.preventDefault();
          this.retrySearch();
          return;
        }
        if (event.target.matches('.inline-search__close-button') || event.target.closest('.inline-search__close-button')) {
          this.onFormReset();
          this.close();
          const input = this.input;
          if (document.activeElement === input) input.blur();
          this.closeBlurTimeout = setTimeout(() => {
            this.closeBlurTimeout = null;
            if (document.activeElement === input) input.blur();
          }, 500);
        }
      });
    }

    getQuery() {
      return this.input.value.trim().replace(/\s+/g, ' ');
    }

    captureProductSearch(event) {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey ||
          event.shiftKey || event.altKey) return;
      const link = event.target?.closest?.('a[href]');
      if (!link || !this.predictiveSearchResults.contains(link) || link.hasAttribute('download')) return;
      const target = link.target || document.querySelector('base[target]')?.target;
      if (target && target !== '_self') return;
      const key = 'bt:collection-return:handoff';
      try {
        const destination = new URL(link.href, window.location.origin);
        if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(destination.pathname)) return;
        // A new search replaces the collection beneath the dropdown. Retain
        // only the current query and normal search options, never its facets.
        window.sessionStorage.removeItem(key);
        const query = this.getQuery();
        const form = this.input.form;
        const results = new URL(form.action, window.location.origin);
        const locale = (url) => url.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i)?.[1].toLowerCase() || '';
        if (!this.isOpen || !query || query.length > 200 || query !== this.searchTerm ||
            destination.origin !== window.location.origin || destination.username || destination.password ||
            results.origin !== window.location.origin || results.username || results.password ||
            !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(results.pathname) ||
            locale(destination) !== locale(results) || locale(results) !== locale(window.location) ||
            !/^[A-Z]{3}$/.test(this.dataset.currency || '') || !/^[A-Z]{2}$/.test(this.dataset.country || '')) return;
        results.search = '';
        results.hash = '';
        results.searchParams.set('q', query);
        for (const name of ['type', 'options[prefix]', 'options[unavailable_products]']) {
          const input = form.querySelector(`input[type="hidden"][name="${name}"]`);
          if (input?.value) results.searchParams.set(name, input.value);
        }
        const written = Date.now();
        window.sessionStorage.setItem(key, JSON.stringify({
          version: 1, from: window.location.pathname, to: destination.pathname, written,
          context: { version: 1, origin: 'predictive-search', href: results.pathname + results.search,
            product: destination.pathname, created: written, depth: 1, anchor: null,
            currency: this.dataset.currency, country: this.dataset.country },
        }));
      } catch (_) { /* Searching and opening products never depend on optional storage. */ }
    }

    onInput() {
      // Old suggestions must not remain actionable during the debounce window.
      if (this.getQuery() !== this.searchTerm) this.close();
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.classList.add('opacity-20');
      }

      if(newSearchTerm.length === 0){
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      const query = this.getQuery();
      this.input.value = query;
      if (!query.length) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      this.searchTerm = '';
      this.close(true);
    }

    onFocus() {
      if (this.closeBlurTimeout !== null && this.closeBlurTimeout !== undefined) {
        clearTimeout(this.closeBlurTimeout);
        this.closeBlurTimeout = null;
      }
      const currentSearchTerm = this.getQuery();

      if (window.visualViewport) {
        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 100);

        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 500);
      }

      if (!currentSearchTerm.length) return;
      if (this.searchTerm === currentSearchTerm && this.hasAttribute('loading')) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onKeyup(event) {
      if (event.target !== this.input || event.isComposing || event.keyCode === 229) return;
      if (!this.getQuery().length) this.close(true);

      switch (event.key || event.code) {
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
      }
    }

    onKeydown(event) {
      const key = event.key || event.code;
      if (event.isComposing || event.keyCode === 229) return;

      if (key === 'Escape' && this.isOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }

      if (event.target !== this.input) return;

      // Composite navigation belongs to the combobox, not the form's buttons.
      if (key === 'ArrowUp' || key === 'ArrowDown') event.preventDefault();

      if (key === 'Enter' && this.isOpen && this.querySelector('[aria-selected="true"]')) {
        // Cancel implicit submission before it fires; keyup must not activate twice.
        event.preventDefault();
        if (!event.repeat) this.selectOption();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
          (element) => element.offsetParent !== null
      );
      if (!allVisibleElements.length) return;
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
      activeElement.scrollIntoView({ block: 'nearest' });
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    getResultPrefix() {
      return (this.dataset.resultPrefix || this.input.id || 'PredictiveSearch')
        .replace(/[^a-zA-Z0-9_-]/g, '-');
    }

    getScopedResultId(suffix) {
      return `${this.getResultPrefix()}-${suffix}`;
    }

    normalizeBrandDirectoryTerm(value) {
      const source = String(value || '');
      const normalized = typeof source.normalize === 'function' ? source.normalize('NFD') : source;
      return normalized
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
    }

    collectionUrlKey(value) {
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !/\/collections\/[^/]+$/.test(url.pathname)) return '';
        return url.pathname;
      } catch (_) {
        return '';
      }
    }

    loadBrandDirectory() {
      const directoryUrl = this.dataset.brandDirectoryUrl;
      if (!directoryUrl) return Promise.resolve([]);

      const directory = new URL(directoryUrl, window.location.origin);
      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewThemeId && !directory.searchParams.has('preview_theme_id')) {
        directory.searchParams.set('preview_theme_id', previewThemeId);
      }
      const directoryHref = directory.href;
      if (PredictiveSearch.brandDirectoryHref === directoryHref && PredictiveSearch.brandDirectoryPromise) {
        return PredictiveSearch.brandDirectoryPromise;
      }

      PredictiveSearch.brandDirectoryHref = directoryHref;
      PredictiveSearch.brandDirectoryPromise = fetch(directoryHref, {
        credentials: 'same-origin',
        signal: AbortSignal.timeout(15000),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Brand directory could not be loaded');
          return response.text();
        })
        .then((markup) => {
          const directory = new DOMParser().parseFromString(markup, 'text/html');
          const seen = new Set();
          const entries = [];

          directory.querySelectorAll('script[data-brincatoys-brand-search-index]').forEach((index) => {
            let indexedEntries;
            try {
              indexedEntries = JSON.parse(index.textContent || '[]');
            } catch (_) {
              return;
            }
            if (!Array.isArray(indexedEntries)) return;

            indexedEntries.forEach((entry) => {
              const label = String(entry?.label || '').trim();
              const url = this.collectionUrlKey(entry?.url);
              const state = entry?.state;
              if (!label || !url || seen.has(url) || !['available', 'pending'].includes(state)) return;
              seen.add(url);
              entries.push({ label, url, state });
            });
          });

          return entries.sort((left, right) => left.label.localeCompare(right.label, document.documentElement.lang, { sensitivity: 'base' }));
        })
        .catch(() => []);

      return PredictiveSearch.brandDirectoryPromise;
    }

    verifyPendingBrandDirectoryEntry(entry) {
      if (entry.state === 'available') return Promise.resolve(entry);

      const collectionUrl = new URL(entry.url, window.location.origin);
      collectionUrl.pathname = `${collectionUrl.pathname.replace(/\/$/, '')}/products.json`;
      collectionUrl.search = 'limit=250';

      return fetch(collectionUrl.href, {
        credentials: 'same-origin',
        signal: AbortSignal.timeout(15000),
      })
        .then((response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .then((payload) => {
          if (!Array.isArray(payload?.products)) return null;
          return payload.products.some((product) => product.variants?.some((variant) => variant.available === true))
            ? { ...entry, state: 'available' }
            : null;
        })
        .catch(() => null);
    }

    getBrandDirectoryMatches(searchTerm) {
      const terms = this.normalizeBrandDirectoryTerm(searchTerm).split(' ').filter(Boolean);
      if (!terms.length) return Promise.resolve([]);

      const requestedLimit = Number.parseInt(this.dataset.brandDirectoryLimit, 10);
      const limit = Number.isFinite(requestedLimit) && requestedLimit > 0 ? requestedLimit : 6;

      return this.loadBrandDirectory()
        .then((entries) => entries
          .filter((entry) => {
            const name = this.normalizeBrandDirectoryTerm(entry.label);
            return terms.every((term) => name.includes(term));
          })
          .slice(0, limit * 2))
        .then((entries) => Promise.all(entries.map((entry) => this.verifyPendingBrandDirectoryEntry(entry))))
        .then((entries) => entries.filter(Boolean));
    }

    createBrandDirectoryCollectionList(results) {
      let wrapper = results.querySelector('.predictive-search__results-groups-wrapper');
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products';
        results.querySelector('.predictive-search__search-no-results')?.remove();
        results.insertBefore(wrapper, results.querySelector('.predictive-search__view-all'));
      }
      wrapper.classList.remove('predictive-search__results-groups-wrapper--no-suggestions');

      const group = document.createElement('div');
      group.className = 'predictive-search__result-group predictive-search__result-group-right';
      const pages = document.createElement('div');
      pages.className = 'predictive-search__pages-wrapper';
      const heading = document.createElement('h2');
      heading.id = this.getScopedResultId('predictive-search-brand-directory');
      heading.className = 'predictive-search__heading text-body t3';
      heading.textContent = this.dataset.brandDirectoryCollectionsLabel || 'Collections';
      const list = document.createElement('ul');
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);
      list.setAttribute('data-predictive-search-collections', '');
      pages.append(heading, list);
      group.append(pages);
      wrapper.append(group);
      return list;
    }

    appendBrandDirectoryResults(entries) {
      if (!entries.length) return false;

      const results = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (!results) return false;

      let list = results.querySelector('[data-predictive-search-collections]');
      const existingUrls = new Set(
        Array.from(results.querySelectorAll('a[href*="/collections/"]'))
          .map((link) => this.collectionUrlKey(link.getAttribute('href')))
          .filter(Boolean),
      );
      const existingSize = list ? list.querySelectorAll('[role="option"]').length : 0;
      const requestedLimit = Number.parseInt(this.dataset.brandDirectoryLimit, 10);
      const limit = Number.isFinite(requestedLimit) && requestedLimit > 0 ? requestedLimit : 6;
      const additions = entries
        .filter((entry) => !existingUrls.has(entry.url))
        .slice(0, Math.max(0, limit - existingSize));
      if (!additions.length) return false;

      if (!list) list = this.createBrandDirectoryCollectionList(results);

      additions.forEach((entry, index) => {
        const item = document.createElement('li');
        item.id = this.getScopedResultId(`predictive-search-option-brand-${existingSize + index + 1}`);
        item.className = 'predictive-search__list-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('data-predictive-search-collection-url', entry.url);

        const link = document.createElement('a');
        link.href = entry.url;
        link.className = 'predictive-search__item link link--text';
        link.tabIndex = -1;

        const content = document.createElement('div');
        content.className = 'predictive-search__item-content predictive-search__item-content--centered';
        const title = document.createElement('p');
        title.className = 'predictive-search__item-heading';
        title.textContent = entry.label;
        content.append(title);
        link.append(content);
        item.append(link);
        list.append(item);
      });

      return true;
    }

    enrichBrandDirectoryResults(searchTerm, requestSequence) {
      if (!this.dataset.brandDirectoryUrl) return;
      if (/(?:^|\D)\d{5}(?!\d)/.test(searchTerm)) return;

      this.getBrandDirectoryMatches(searchTerm)
        .then((entries) => {
          if (requestSequence !== this.requestSequence || !this.hasAttribute('results')) return;
          if (this.appendBrandDirectoryResults(entries)) {
            this.setLiveRegionText(this.dataset.brandDirectoryStatus || this.dataset.brandDirectoryCollectionsLabel || 'Collections');
            if (window.updateViewportSize) window.updateViewportSize();
          }
        })
        .catch(() => {});
    }

    appendReferenceProduct(product, reference) {
      const results = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (!results) return false;

      const productUrl = new URL(product.url, window.location.origin);
      if (productUrl.origin !== window.location.origin || !productUrl.pathname.includes('/products/')) return false;
      const alreadyShown = [...results.querySelectorAll('a[href*="/products/"]')].some((link) =>
        new URL(link.href, window.location.origin).pathname === productUrl.pathname
      );
      if (alreadyShown) return false;

      const group = document.createElement('div');
      group.className = 'predictive-search__result-group bt-predictive-reference';
      const heading = document.createElement('h2');
      heading.id = this.getScopedResultId('predictive-search-reference');
      heading.className = 'predictive-search__heading text-body t3';
      heading.textContent = this.dataset.referenceLabel || 'LEGO';
      const list = document.createElement('ul');
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);
      const item = document.createElement('li');
      item.id = this.getScopedResultId('predictive-search-option-reference');
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      const link = document.createElement('a');
      link.href = productUrl.pathname;
      link.className = 'predictive-search__item link link--text';
      link.tabIndex = -1;

      const imageUrl = product.featured_image?.url;
      if (imageUrl) {
        const image = new URL(imageUrl, window.location.origin);
        if (image.protocol === 'https:' && image.hostname === 'cdn.shopify.com') {
          image.searchParams.set('width', '128');
          const thumbnail = document.createElement('img');
          thumbnail.className = 'predictive-search__image';
          thumbnail.src = image.href;
          thumbnail.alt = '';
          thumbnail.width = 64;
          thumbnail.height = 64;
          thumbnail.loading = 'eager';
          thumbnail.decoding = 'async';
          link.classList.add('predictive-search__item--link-with-thumbnail');
          link.append(thumbnail);
        }
      }

      const content = document.createElement('div');
      content.className = 'predictive-search__item-content';
      const title = document.createElement('p');
      title.className = 'predictive-search__item-heading t3';
      title.textContent = product.title;
      const model = document.createElement('span');
      model.className = 'predictive-search__reference';
      model.textContent = `LEGO ${reference}`;
      content.append(title, model);
      link.append(content);
      item.append(link);
      list.append(item);
      group.append(heading, list);
      results.querySelector('.predictive-search__search-no-results')?.remove();
      results.prepend(group);
      return true;
    }

    enrichReferenceResults(searchTerm, requestSequence, signal) {
      if (this.productTypes?.value) return;
      const references = [...searchTerm.matchAll(/(?:^|\D)(\d{5})(?!\d)/g)].map((match) => match[1]);
      if (references.length !== 1) return;

      const root = window.Shopify?.routes?.root || '/';
      const endpoint = new URL(`${root}search/suggest.json`, window.location.origin);
      endpoint.searchParams.set('q', `variants.sku:66${references[0]}*`);
      endpoint.searchParams.set('resources[type]', 'product');
      endpoint.searchParams.set('resources[limit]', '3');
      endpoint.searchParams.set('resources[options][fields]', 'variants.sku');

      fetch(endpoint, { signal })
        .then((response) => response.ok ? response.json() : null)
        .then((data) => {
          if (requestSequence !== this.requestSequence || !this.hasAttribute('results')) return;
          const matches = (data?.resources?.results?.products || []).filter((product) =>
            product.vendor?.toLowerCase() === 'lego' && product.title && product.url
          );
          if (matches.length !== 1) return;
          if (this.appendReferenceProduct(matches[0], references[0])) {
            this.setLiveRegionText(this.dataset.referenceStatus || this.dataset.referenceLabel || 'LEGO');
            if (window.updateViewportSize) window.updateViewportSize();
          }
        })
        .catch(() => {});
    }

    getSearchResults(searchTerm) {
      if (this.hasAttribute('search-error')) this.close();
      const parametersForm = this.dataset.parametersForm
        ? document.getElementById(this.dataset.parametersForm)
        : null;
      const formData = parametersForm ? new FormData(parametersForm) : new FormData();
      const params = new URLSearchParams(formData);

      this.abortController.abort();
      this.abortController = new AbortController();
      const requestController = this.abortController;
      const requestSequence = ++this.requestSequence;
      const directorySearchTerm = searchTerm;

      if (this.productTypes && this.productTypes.value.length > 0) {
        searchTerm = `product_type:${this.productTypes.value} AND ${searchTerm}`;
      }

      params.set('q', searchTerm);

      const urlParams = params.toString();
      const queryKey = urlParams.toLowerCase();

      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        this.enrichBrandDirectoryResults(directorySearchTerm, requestSequence);
        this.enrichReferenceResults(directorySearchTerm, requestSequence, requestController.signal);
        return;
      }

      return fetch(`${routes.predictive_search_url}?${urlParams}`, {
        signal: requestController.signal,
      })
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              throw error;
            }

            return response.text();
          })
          .then((text) => {
            if (requestSequence !== this.requestSequence) return;

            const responseSection = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('#shopify-section-predictive-search');
            if (!responseSection) throw new Error('Predictive search section missing');

            const resultsMarkup = responseSection.innerHTML;
            // Save bandwidth keeping the cache in all instances synced
            document.querySelectorAll('predictive-search').forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
            this.enrichBrandDirectoryResults(directorySearchTerm, requestSequence);
            this.enrichReferenceResults(directorySearchTerm, requestSequence, requestController.signal);
          })
          .catch((error) => {
            if (error?.name === 'AbortError' || error?.code === 20 || requestSequence !== this.requestSequence) return;
            this.showSearchError();
          });
    }

    showSearchError() {
      this.close();
      const template = this.querySelector('[data-predictive-search-error-template]');
      if (template?.content) {
        this.predictiveSearchResults.replaceChildren(template.content.cloneNode(true));
        this.input.removeAttribute('aria-controls');
        const message = this.predictiveSearchResults.querySelector('[data-predictive-search-error-message]');
        if (message) {
          this.errorDescriptionId = this.getScopedResultId('Error');
          message.id = this.errorDescriptionId;
          const descriptions = (this.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
          this.input.setAttribute('aria-describedby', [...new Set([...descriptions, this.errorDescriptionId])].join(' '));
        }
        // This panel offers recovery, not selectable search results.
        this.setAttribute('search-error', true);
        this.isOpen = true;
      }
      this.setLiveRegionText(this.dataset.errorText || 'Search suggestions are temporarily unavailable.');
    }

    retrySearch() {
      this.onChange();
      this.input.focus();
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.querySelector('.search-suggestions')?.classList.add('hidden');
      this.input.removeAttribute('aria-activedescendant');

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.scopeResultIds();
      this.predictiveSearchResults.scrollTop = 0;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
      if (window.updateViewportSize) window.updateViewportSize();
    }

    scopeResultIds() {
      const prefix = this.getResultPrefix();
      const idMap = new Map();

      this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
        const originalId = element.id;
        const scopedId = `${prefix}-${originalId}`;
        idMap.set(originalId, scopedId);
        element.id = scopedId;
      });

      ['aria-labelledby', 'aria-controls'].forEach((attribute) => {
        this.predictiveSearchResults.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const value = element.getAttribute(attribute);
          if (!value) return;
          element.setAttribute(attribute, value.split(' ').map((id) => idMap.get(id) || id).join(' '));
        });
      });

      const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (listbox) this.input.setAttribute('aria-controls', listbox.id);
      else this.input.removeAttribute('aria-controls');
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      const resultCount = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (resultCount) this.setLiveRegionText(resultCount.textContent);
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      this.removeAttribute('search-error');
      if (this.errorDescriptionId) {
        const descriptions = (this.input.getAttribute('aria-describedby') || '').split(/\s+/)
          .filter((id) => id && id !== this.errorDescriptionId);
        if (descriptions.length) this.input.setAttribute('aria-describedby', descriptions.join(' '));
        else this.input.removeAttribute('aria-describedby');
        this.errorDescriptionId = null;
      }
      if (clearSearchTerm) {
        this.input.value = '';
        this.toggleResetButton();
      }
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.removeAttribute('aria-activedescendant');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      this.predictiveSearchResults.removeAttribute('style');

      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.requestSequence += 1;
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});
